/**
 * The state machines, as machines rather than as a flat table.
 *
 * /states used to fall through to the generic EntityList, which listed every
 * state as a sibling row - true of the database and false of the model. A
 * sub-state is DECLARED INSIDE its machine, and until issue #14 nothing
 * recorded that, so the table was not hiding the structure so much as there
 * being none to hide.
 *
 * Transitions are fetched PER SELECTION rather than for the whole tree. They
 * are on the detail payload and nowhere else - a transition has no name to
 * look one up by, so nothing lists them - and nothing needs them until somebody
 * looks at that state.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem } from '@mui/x-tree-view/TreeItem'
import { ApiError, api } from '../api'
import { findKind } from '../schema'
import type { EntityRow, KindSchema, StateRow } from '../schema'
import { useCanWrite, useSchema } from '../state/AppData'
import { MONOSPACE } from '../theme'
import EmptyState from '../components/common/EmptyState'
import CreateStateDialog from '../components/states/CreateStateDialog'
import EntityActions from '../components/entity/EntityActions'
import EntityFilterBar from '../components/entity/EntityFilterBar'
import { matchesTerms, searchableText, readSearch, writeSearch } from '../components/entity/rowFilter'
import { useRememberedFilters } from '../components/entity/rememberedFilters'
import StateTransitions from '../components/states/StateTransitions'
import LinkedRequirements from '../components/links/LinkedRequirements'
import StateMapPanel from '../components/states/StateMapPanel'

export interface StateNode {
  row: EntityRow
  children: StateNode[]
}

/**
 * Nest the flat listing by its owner field.
 *
 * Exported for its own test. The mistake this exists to prevent is rebuilding
 * the hierarchy from qualified names: A::B::C looks like it carries the tree
 * and does not, because a prefix match cannot tell a sub-state from a sibling
 * declared in a package that happens to share the name. Issue #14 added the
 * edge precisely so this can follow one.
 *
 * A row whose owner is not in the listing is treated as a ROOT rather than
 * dropped - that happens when a page limit cut the parent out, and silently
 * losing the child would be worse than showing it at the top.
 */
export function toStateTree(rows: EntityRow[]): StateNode[] {
  const nodes = new Map<string, StateNode>()
  for (const row of rows) nodes.set(row.id, { row, children: [] })

  const roots: StateNode[] = []
  for (const row of rows) {
    const node = nodes.get(row.id) as StateNode
    const owner = row.owner ? String(row.owner) : ''
    const parent = owner ? nodes.get(owner) : undefined
    // Never its own parent. A self-referential owner would recurse forever in
    // the renderer, and the column is nullable exactly so it can say "none".
    if (parent && parent !== node) parent.children.push(node)
    else roots.push(node)
  }
  return roots
}

/** Every id in a tree, for expanding it all at once. */
function allIds(nodes: StateNode[]): string[] {
  return nodes.flatMap((node) => [node.row.id, ...allIds(node.children)])
}

/** Keep a node when it or anything below it matches. */
function prune(nodes: StateNode[], text: string): StateNode[] {
  const keep = (node: StateNode): StateNode | null => {
    const children = node.children.map(keep).filter(Boolean) as StateNode[]
    const hit = matchesTerms(searchableText(node.row), text)
    return hit || children.length ? { row: node.row, children } : null
  }
  return nodes.map(keep).filter(Boolean) as StateNode[]
}

/**
 * The parts a row says exhibit it.
 *
 * Tolerates a bare string as well as a list. The field became [0..*] in issue
 * #14 and a cached or older payload can still carry the single-value shape;
 * rendering that as the characters of a string is the failure this avoids.
 */
export function exhibitors(row: EntityRow): string[] {
  return referenceList(row, 'exhibited_by')
}

/** A reference-list field as strings, whether the API sent a list or one value. */
function referenceList(row: EntityRow, key: string): string[] {
  const value = row[key]
  if (Array.isArray(value)) return value.map(String)
  return value ? [String(value)] : []
}

export default function States() {
  const kind = findKind(useSchema(), 'states')
  const [rows, setRows] = useState<EntityRow[] | null>(null)
  // Bumped after a create or an import, so the tree re-reads the listing.
  const [generation, setGeneration] = useState(0)
  const [error, setError] = useState<ApiError | null>(null)
  /**
   * The search, in the URL under `search` as on every other list (issue #86).
   *
   * It was component state, so it was gone the moment anything else was opened
   * and the box was empty on the way back. In the URL it is a link, and
   * remembered so that the sidebar and the breadcrumbs put it back. Replace, not
   * push: typing must not fill up the back button.
   */
  const [params, setParams] = useSearchParams()
  const search = readSearch(params)
  const setSearch = (next: string) =>
    setParams((now) => writeSearch(now, next), { replace: true })
  useRememberedFilters()
  const [selected, setSelected] = useState<string>('')
  // The machines one at a time, or every transition at once (burndown Phase 8).
  const [pane, setPane] = useState<'states' | 'map'>('states')
  const [detail, setDetail] = useState<StateRow | null>(null)
  // Bumped after a write so the detail refetches. The projection is re-derived
  // from the file on every save, so what comes back is what the model now
  // says rather than what was sent - which is the only way to see that a new
  // end resolved.
  const [reloads, setReloads] = useState(0)
  const reload = useCallback(() => setReloads((count) => count + 1), [])

  useEffect(() => {
    api
      .list('states', { limit: 5000 })
      .then((page) => setRows(page.items))
      .catch((failure: ApiError) => setError(failure))
  }, [generation])

  const created = useCallback((identifier: string) => {
    setGeneration((count) => count + 1)
    setSelected(identifier)
  }, [])

  useEffect(() => {
    if (!selected) {
      setDetail(null)
      return
    }
    let current = true
    // Guarded against the RESPONSE ORDER, not merely against unmounting: click
    // three states quickly and the replies may land in any order, so the last
    // one to ARRIVE would otherwise win rather than the last one clicked.
    setDetail(null)
    api
      .state(selected)
      .then((found) => {
        if (current) setDetail(found)
      })
      .catch(() => {
        if (current) setDetail(null)
      })
    return () => {
      current = false
    }
  }, [selected, reloads])

  const tree = useMemo(() => (rows ? toStateTree(rows) : []), [rows])
  const shown = useMemo(() => (search ? prune(tree, search) : tree), [tree, search])
  const expanded = useMemo(() => allIds(shown), [shown])

  const onSelect = useCallback((_event: React.SyntheticEvent | null, id: string | null) => {
    setSelected(id ?? '')
  }, [])

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!rows) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">States</Typography>
          <Typography variant="body2" color="text.secondary">
            Each machine with the states declared inside it. Select one to see the transitions out
            of it and the parts that exhibit it.
          </Typography>
        </Box>
        {kind && (
          <EntityActions
            kind={kind}
            onCreated={created}
            onImported={() => setGeneration((count) => count + 1)}
          />
        )}
      </Stack>

      <Tabs value={pane} onChange={(_event, next) => setPane(next)}>
        <Tab value="states" label="States" />
        <Tab value="map" label="All transitions" />
      </Tabs>

      {pane === 'map' ? <StateMapPanel /> : (
      <>
      <EntityFilterBar plural="states" value={search} onChange={setSearch} hint="machines kept" />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            {shown.length === 0 ? (
              <EmptyState
                title={search ? `Nothing matches ${search}` : 'No states'}
                description={search ? undefined : 'Use “Import from GitLab…” in the program menu to import a model.'}
              />
            ) : (
              <SimpleTreeView
                // Re-mounted when the filter changes so the expansion follows
                // the result rather than whatever was open before.
                key={search}
                defaultExpandedItems={expanded}
                onSelectedItemsChange={onSelect}
                sx={{ '& .MuiTreeItem-content': { py: 0.35 } }}
              >
                {shown.map((node) => (
                  <Branch key={node.row.id} node={node} />
                ))}
              </SimpleTreeView>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Detail
              row={detail}
              selected={selected}
              onSaved={reload}
              kind={kind}
              qualifiedName={String(rows.find((row) => row.id === selected)?.qualified_name ?? '')}
              onCreated={created}
              states={rows}
            />
          </Paper>
        </Grid>
      </Grid>
      </>
      )}
    </Stack>
  )
}

function Branch({ node }: { node: StateNode }) {
  const { row } = node
  // A USAGE rather than a definition. Shown because the two are different
  // claims and a reader looking at a machine needs to know which it has: a
  // def can be typed and reused, a usage cannot.
  const isUsage = row.is_definition === false

  return (
    <TreeItem
      itemId={row.id}
      label={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
          <RouterLink
            to={`/states/${encodeURIComponent(row.id)}`}
            style={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}
            onClick={(event) => event.stopPropagation()}
          >
            {row.id}
          </RouterLink>
          {/* Mode and phase are KINDS of state, so they read before a role like "initial". */}
          {row.is_mode === true && (
            <Chip label="mode" size="small" color="secondary" sx={{ height: 20 }} />
          )}
          {row.is_phase === true && (
            <Chip label="phase" size="small" color="info" sx={{ height: 20 }} />
          )}
          {row.is_initial === true && (
            <Chip label="initial" size="small" color="primary" sx={{ height: 20 }} />
          )}
          {isUsage && (
            // Its def, when it has one: a machine holds usages of reusable defs
            // (decision 030), and the def is where the facts are stated.
            <Chip
              label={row.definition ? `usage of ${String(row.definition)}` : 'usage'}
              size="small"
              variant="outlined"
              sx={{ height: 20 }}
            />
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {String(row.statement ?? row.name ?? '')}
          </Typography>
        </Stack>
      }
    >
      {node.children.map((child) => (
        <Branch key={child.row.id} node={child} />
      ))}
    </TreeItem>
  )
}

function Detail({
  row,
  selected,
  onSaved,
  kind,
  qualifiedName,
  onCreated,
  states,
}: {
  row: StateRow | null
  selected: string
  onSaved: () => void
  kind: KindSchema | undefined
  qualifiedName: string
  onCreated: (identifier: string) => void
  states: EntityRow[]
}) {
  const { allowed, reason } = useCanWrite()
  const [creating, setCreating] = useState(false)

  if (!selected) {
    return <EmptyState title="Select a state" description="Its transitions appear here." />
  }
  if (!row) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  const parts = exhibitors(row)
  const included = referenceList(row, 'includes')
  const definition = row.definition ? String(row.definition) : ''
  // Said beside a list that is the def's, so it is not read as this usage's own.
  const fromDef = (field: string) =>
    definition && row.inherited?.includes(field) ? ` (from ${definition})` : ''
  // Only a state DEFINITION may own another state - OWNING_KEYWORDS maps
  // 'state' to ('state def',) - so a usage is not offered the control at all
  // rather than offered one the server refuses.
  const mayOwn = Boolean(kind?.can_create && row.is_definition !== false && qualifiedName)
  const label = String(row.name ?? row.id)

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontFamily: MONOSPACE }}>
            {row.id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {String(row.statement ?? row.name ?? '')}
          </Typography>
          {definition && (
            <Typography variant="body2">
              Usage of{' '}
              <RouterLink to={`/states/${encodeURIComponent(definition)}`} style={{ fontFamily: MONOSPACE }}>
                {definition}
              </RouterLink>
            </Typography>
          )}
        </Box>
        {mayOwn && (
          <Tooltip title={allowed ? '' : reason} describeChild>
            <span>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreating(true)}
                disabled={!allowed}
              >
                New state inside
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {creating && kind && (
        <CreateStateDialog
          inside={{ qualifiedName, label }}
          onClose={() => setCreating(false)}
          onCreated={(identifier) => {
            setCreating(false)
            onCreated(identifier)
          }}
        />
      )}

      {(row.entries?.length ?? 0) > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            Starts in
          </Typography>
          <Stack spacing={0.5}>
            {row.entries?.map((entry) => (
              <Typography key={`${entry.line}-${entry.to}`} variant="body2">
                <span style={{ fontFamily: MONOSPACE }}>{entry.to}</span>
                {entry.guard
                  ? ` if ${entry.guard}`
                  : row.entries?.some((other) => other.guard)
                    ? ' otherwise'
                    : ''}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {row.initial_is_implicit && (
        // Honoured AND reported: the machine states no entry, so this is only
        // where it starts by default (Patrick, 2026-09-15). Said here so the
        // chip in the tree is not read as somebody's decision.
        <Alert severity="info" variant="outlined">
          Initial only by default: its machine states no <code>entry</code>, so it starts in its
          first sub-state.
        </Alert>
      )}

      <Box>
        <Typography variant="overline" color="text.secondary">
          Exhibited by{fromDef('exhibited_by')}
        </Typography>
        {parts.length === 0 ? (
          // UNSTATED, and it says so rather than rendering an empty row. A
          // state may legitimately be drafted before the part that owns it
          // exists, which is why the feature's lower bound is 0.
          <Typography variant="body2" color="text.secondary">
            No part stated.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {parts.map((part) => (
              <Chip key={part} label={part} size="small" sx={{ fontFamily: MONOSPACE }} />
            ))}
          </Stack>
        )}
      </Box>

      {included.length > 0 && (
        // The component states that hold while this one does - issue #42.
        // Only when stated: naming none is the ordinary case for most states,
        // so an empty box here would be noise rather than information.
        <Box>
          <Typography variant="overline" color="text.secondary">
            Includes{fromDef('includes')}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {included.map((state) => (
              <Chip key={state} label={state} size="small" sx={{ fontFamily: MONOSPACE }} />
            ))}
          </Stack>
        </Box>
      )}

      {(row.usages?.length ?? 0) > 0 && (
        // Where a def is used: one def may sit in several machines (decision 030).
        <Box>
          <Typography variant="overline" color="text.secondary">
            Used in
          </Typography>
          <Stack spacing={0.5}>
            {row.usages?.map((usage) => (
              <Typography key={usage.qualified_name} variant="body2" sx={{ fontFamily: MONOSPACE }}>
                {usage.machine.split('::').pop()}::{usage.qualified_name.split('::').pop()}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {/* The requirements that link to this state (issue #48). */}
      <LinkedRequirements key={`links-${row.id}`} kind="state" identifier={row.id} />

      <Box>
        <Typography variant="overline" color="text.secondary">
          Transitions
        </Typography>
        {/* What goes out and what comes in, wherever each is declared (issue #50). */}
        <StateTransitions key={row.id} row={row} states={states} onSaved={onSaved} />
      </Box>
    </Stack>
  )
}
